import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  InputAdornment
} from '@mui/material'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { ChronoTextField, ChronoIconButton } from '@eventops/chronotrack-ctlive-mui'
import useAppDispatch from '../../../hooks/useAppDispatch'
import useAppSelector from '../../../hooks/useAppSelector'
import { changeFilterTerm, getFilterTermSelector } from '../filterSlice'
import { useStyles } from '../../../hooks/useStyles'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import useCheckCameraAccess from '../../camera/hooks/useCheckCameraAccess'
import { createLogger } from '../../../app/logger'
import useDebounce from '../../../hooks/useDebounce'
import {
  parseJson,
  doAtob
} from '../../../utils/decoder'
import { getS3EntriesSelector } from '../../data/dataSlice'
import { useEntries } from '../../checkin/hooks/useEntries'
import { convertToCheckinEntry } from '../../checkin/hooks/useCheckinDataColumns'
import { setSelectedEntry } from '../../checkin/checkinSlice'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'ChronoFilterTextField'
})

export function ChronoFilterTextField() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const term = useAppSelector(getFilterTermSelector) || ''
  const [value, setValue] = React.useState(term)
  const debouncedValue = useDebounce(value, value ? 1000 : 0)
  const prevValue = useRef<string>()
  const checkCameraAccess = useCheckCameraAccess()
  const { classes } = useStyles()

  const entriesFromApi = useAppSelector(getS3EntriesSelector)
  const entries = useEntries(entriesFromApi)
  const mappedEntries = useMemo(() => entries?.map(convertToCheckinEntry), [entries])

  useEffect(() => {
    const decoded = doAtob(debouncedValue)
    let customFilter: string | undefined
    if (decoded) {
      const parsed = parseJson(decoded)
      if (parsed) {
        if (Object.hasOwn(parsed, 'entryId')) {
          customFilter = String(parsed.entryId)
        }
      }
    }

    const updatedValue = customFilter ?? debouncedValue
    dispatch(changeFilterTerm(updatedValue))
    if (customFilter) {
      setValue(customFilter)
    }

    if (prevValue.current !== updatedValue) {
      const entry = mappedEntries.find(entry => String(entry.entryId) === updatedValue || (entry.entryExternalId && entry.entryExternalId === updatedValue))
      if (entry) {
        dispatch(setSelectedEntry(entry))
        // clear out search input after entry id match so users can search quickly for next id (ie. handheld scanners)
        setValue('')
      }
    }

    prevValue.current = updatedValue
  }, [
    debouncedValue,
    dispatch,
    mappedEntries
  ])

  return (
    <Box
      data-cy={'text-search'}
      className={classes.textSearchField}
    >
      <ChronoTextField
        autoFocus={true}
        fullWidth
        label={t('Search By Name or Bib/Tag') ?? ''}
        variant={'outlined'}
        value={value}
        onChange={value => {
          setValue(String(value))
        }}
        InputProps={{
          endAdornment: (
            <>
              {term &&
                <CancelOutlinedIcon
                  className={classes.cancelIcon}
                  onClick={() => {
                    setValue('')
                  }}
                />
              }
              <InputAdornment position='end'>
                <ChronoIconButton
                  onClick={() =>
                    checkCameraAccess()
                      .catch((err) => {
                        logger.error('error while opening QR', err)
                      })}
                  Icon={CameraAltOutlinedIcon}
                />
              </InputAdornment>
            </>
          )
        }}
      />
    </Box>
  )
}
